<template>
  <div class="single-select-v2-wrapper">
    <!-- Label -->
    <label v-if="label" class="field-label">{{ label }}</label>

    <!-- Select Trigger -->
    <div class="select-trigger" :class="{ active: isOpen }">
      <input
        v-if="isOpen"
        v-model="searchQuery"
        type="text"
        :placeholder="placeholder"
        class="select-input"
        @focus="openDropdown"
        @input="onSearchInput"
        :disabled="disabled"
      />
      <input
        v-else
        :value="selectedLabel"
        type="text"
        :placeholder="placeholder"
        class="select-input"
        @focus="openDropdown"
        :disabled="disabled"
        readonly
      />
      <div
        @click="toggleDropdown"
        v-if="!disabled"
        class="select-arrow"
        :class="{ open: isOpen, 'select-arrow-disabled': disabled }"
      >
        <i class="fa fa-chevron-down"></i>
      </div>
    </div>

    <!-- Dropdown Menu -->
    <div v-if="isOpen" class="dropdown-menu-container">
      <!-- Options List -->
      <div class="options-list">
        <div v-if="filteredOptions.length === 0" class="no-options">
          Tidak ada data
        </div>
        <div
          v-for="(option, index) in filteredOptions"
          :key="index"
          :class="[
            'option-item',
            {
              disabled: option.disabled,
              selected: option.value === selectedValue,
              highlighted: index === highlightedIndex,
            },
          ]"
          @click="option.disabled ? null : selectOption(option)"
          @mouseover="highlightedIndex = index"
        >
          <div
            v-if="option.additionalMessage"
            class="w-100 d-flex justify-content-between align-items-center"
          >
            <span>{{ option.label }}</span>
            <span class="additional-message">
              <img
                class="mr-1"
                width="12"
                src="@/assets/icon/warning-circle-grey.svg"
                alt="icon"
              />
              {{ option.additionalMessage }}
            </span>
          </div>
          <div v-else class="option-label">
            {{ option.label }}
          </div>
        </div>
      </div>
    </div>

    <!-- Backdrop -->
    <div v-if="isOpen" class="backdrop" @click="closeDropdown" />
  </div>
</template>

<script>
import { debounce } from "lodash";

export default {
  name: "SingleSelectV2",
  props: {
    value: {
      type: [String, Number],
      default: "",
    },
    options: {
      type: Array,
      default: () => [],
      validator(value) {
        return value.every(
          (item) =>
            typeof item === "object" && "label" in item && "value" in item
        );
      },
    },
    label: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "Pilih Nama Lessee Awal",
    },
    searchPlaceholder: {
      type: String,
      default: "Cari...",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      searchQuery: "",
      highlightedIndex: -1,
    };
  },
  computed: {
    selectedValue() {
      return this.value;
    },
    selectedLabel() {
      if (this.selectedValue === "" || this.selectedValue === null || this.selectedValue === undefined) {
        return "";
      }

      const selected = this.options.find(
        (opt) => opt.value === this.selectedValue
      );

      return selected ? selected.label : "";
    },
    filteredOptions() {
      if (!this.searchQuery.trim()) {
        return this.options;
      }
      return this.options.filter((option) =>
        option.label.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
  },
  methods: {
    openDropdown() {
      this.isOpen = true;
      this.highlightedIndex = -1;
    },
    closeDropdown() {
      this.isOpen = false;
      this.searchQuery = "";
      this.highlightedIndex = -1;
    },
    toggleDropdown() {
      if (this.disabled) return;
      if (this.isOpen) {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    },
    onSearchInput() {
      this.highlightedIndex = -1;
      // Keep dropdown open while typing
      if (!this.isOpen) {
        this.isOpen = true;
      }
      this.debouncedEmitSearch(this.searchQuery);
    },

    selectOption(option) {
      this.$emit("input", option.value);
      this.$emit("change", option.value);
      // Clear search and close after selection
      this.searchQuery = "";
      this.isOpen = false;
    },
  },
  created() {
    this.debouncedEmitSearch = debounce((query) => {
      this.$emit("search", query);
    }, 400);
  },
};
</script>

<style scoped>
.single-select-v2-wrapper {
  position: relative;
  width: 100%;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  color: #374151;
  font-weight: 600;
  font-size: 14px;
}

/* Select Trigger */
.select-trigger {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
  border: 1px solid #d1d9e2;
  border-radius: 6px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.select-trigger:hover,
.select-trigger.active {
  border-color: #247fec;
}

.select-input {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  border: none;
  background: transparent;
  font-size: 14px;
  color: #2e3646;
  outline: none;
  cursor: text;
}

.select-input[disabled] {
  background-color: #f5f5f5;
  color: #999;
  cursor: not-allowed;
  border-radius: 8px;
}

.select-input::placeholder {
  color: #999;
}

.select-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 12px;
  color: #666;
  transition: transform 0.2s ease;
}

.select-arrow-disabled {
  color: #999;
  background-color: #f5f5f5;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  height: 100%;
}

.select-arrow.open {
  transform: rotate(180deg);
}

.arrow-icon {
  font-size: 12px;
  font-style: normal;
  display: block;
}

/* Dropdown Menu */
.dropdown-menu-container {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background-color: #ffffff;
  border: 1px solid #d1d9e2;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
}

/* Options List */
.options-list {
  max-height: 260px;
  overflow-y: auto;
}

.no-options {
  padding: 12px 16px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.option-item {
  padding: 6px 16px;
  font-size: 14px;
  color: #2e3646;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  border-top: 1px solid #d1d9e266;
  border-bottom: 1px solid #d1d9e266;
}

.option-item:hover {
  background-color: #f8f9fa;
}

.option-item.highlighted {
  background-color: #f0f4ff;
}

.option-item.selected {
  background-color: #f8f9fa;
  font-weight: 500;
}

.option-item.disabled {
  color: #999;
  cursor: not-allowed;
  background-color: #f5f5f5;
}

.additional-message {
  font-size: 10px;
  color: #5a6482;
  margin-left: 8px;
}

/* Backdrop */
.backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
}

/* Scrollbar Styling */
.options-list::-webkit-scrollbar {
  width: 6px;
}

.options-list::-webkit-scrollbar-track {
  background: transparent;
}

.options-list::-webkit-scrollbar-thumb {
  background: #d1d9e2;
  border-radius: 3px;
}

.options-list::-webkit-scrollbar-thumb:hover {
  background: #999;
}
</style>



// WEBPACK FOOTER //
// src/components/Input/SingleSelectV2.vue